"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { motion, AnimatePresence } from "framer-motion";
import { Section } from "@/components/ui/Section";
import { cn } from "@/lib/utils";
import { Link } from "@/i18n/routing";
import {
  portfolioItems,
  portfolioCategories,
  PortfolioCategory,
} from "@/lib/portfolio";
import { ProjectCard } from "@/components/ui/ProjectCard";
import { ArrowRight } from "lucide-react";

export function Portfolio() {
  const t = useTranslations("Index");
  const tPortfolio = useTranslations("Portfolio");
  const [activeCategory, setActiveCategory] =
    useState<PortfolioCategory>("all");

  const filteredItems =
    activeCategory === "all"
      ? portfolioItems
      : portfolioItems.filter((item) => item.category.includes(activeCategory));

  // Limit to 12 items (previously 6)
  const displayedItems = filteredItems.slice(0, 12);

  return (
    <Section
      id="portfolio"
      title={t("portfolio_title")}
      subtitle={t("portfolio_subtitle")}
      className="bg-(--bg-primary)"
      separator={{
        position: "top",
        variant: "wave-curve",
        fillClassName: "fill-(--bg-card)",
      }}
    >
      {/* Premium Segmented Filter Tabs */}
      <div className="flex justify-center mb-16 md:mb-24">
        <div className="inline-flex flex-wrap justify-center gap-1.5 sm:gap-3 p-1.25 sm:p-2.5 rounded-full bg-(--bg-card) border border-(--border-subtle) shadow-sm select-none">
          {portfolioCategories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={cn(
                "relative px-3 sm:px-6 py-1.25 sm:py-2.5 rounded-full text-sm font-bold tracking-wide transition-all duration-300 cursor-pointer", // Added cursor-pointer, font-bold
                activeCategory === category
                  ? "text-(--text-primary)"
                  : "text-(--text-secondary) hover:text-(--brand-primary)",
              )}
            >
              <span className="relative z-10 capitalize">
                {tPortfolio(`filters.${category}`)}
              </span>
              {activeCategory === category && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute inset-0 bg-(--bg-primary) rounded-full shadow-sm"
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Premium Grid Layout */}
      <motion.div
        layout
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12"
      >
        <AnimatePresence mode="popLayout">
          {displayedItems.map((item) => (
            <motion.div
              key={item.key}
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
            >
              <ProjectCard item={item} />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      {/* Pulsating View More Button (Redesigned) */}
      <div className="flex justify-center mt-20">
        <Link
          href="/projects"
          className={cn(
            "group relative flex items-center justify-between pl-8 pr-2 py-2 rounded-full bg-(--brand-primary) text-white font-bold tracking-wider overflow-hidden",
            "animate-pulse-red hover:animate-none hover:shadow-xl hover:shadow-(--brand-primary)/40 transition-all duration-300 gap-8",
          )}
        >
          <span className="text-lg">{tPortfolio("labels.view_more")}</span>
          {/* White circle, Red Arrow */}
          <div className="h-12 w-12 rounded-full bg-white flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
            <ArrowRight className="w-6 h-6 stroke-[3px] text-(--brand-primary)" />
          </div>
        </Link>
      </div>
    </Section>
  );
}
